import { Injectable, inject } from '@angular/core';
import { HttpClient } from "@angular/common/http";
import { lastValueFrom } from "rxjs";
import { environment } from "../../environments/environment";
import { User } from "../administration/users/user.model";
import { AuthService } from "../login/auth.service";
import { Chat, ChatMember, ChatMessage } from "./chat.model";
import { ResponseModel, SingleResponseModel } from "../shared/response.model";
import * as qs from "qs";
import { ChatRealtimeService } from "./chat-realtime.service";

@Injectable({
  providedIn: 'root'
})
export class MessagesService {
  private http = inject(HttpClient);
  private authService = inject(AuthService);
  private chatRealtimeService = inject(ChatRealtimeService);

  async fetchMyChats(): Promise<Chat[]> {
    const currentUserId = await this.currentUserId();
    const query = qs.stringify({
      filters: {
        chat_members: {
          user: {
            id: {
              $eq: currentUserId
            }
          }
        }
      },
      populate: {
        chat_members: {
          populate: {
            user: true
          }
        },
        chat_messages: {
          populate: {
            users_permissions_user: true
          },
          sort: ['sent_at:asc', 'createdAt:asc']
        }
      },
      sort: ['updatedAt:desc'],
      pagination: {
        pageSize: 100
      }
    }, { encodeValuesOnly: true });

    const response = await lastValueFrom(this.http.get<ResponseModel<Chat>>(`${environment.apiUrl}/chats?${query}`));
    return response.data ?? [];
  }

  async createChat(recipients: User[]): Promise<Chat> {
    const currentUserId = await this.currentUserId();
    const recipientIds = recipients
      .map((recipient) => recipient.id)
      .filter((id): id is number => id != null && id !== currentUserId);

    const uniqueRecipientIds = [...new Set(recipientIds)];
    if (uniqueRecipientIds.length === 0) {
      throw new Error('A chat needs at least one recipient.');
    }

    const chat = uniqueRecipientIds.length === 1
      ? await this.findOrCreateDirectChat(currentUserId, uniqueRecipientIds[0])
      : await this.createGroupChat();

    await Promise.all([
      this.ensureChatMember(chat.id, currentUserId, true),
      ...uniqueRecipientIds.map((recipientId) => this.ensureChatMember(chat.id, recipientId, false))
    ]);

    return chat;
  }

  async createChatMessage(chatId: number, message: string): Promise<ChatMessage> {
    await this.chatRealtimeService.sendChatMessage(chatId, message.trim());
    return {} as ChatMessage;
  }

  async markChatAsRead(chatId: number): Promise<void> {
    const currentUserId = await this.currentUserId();
    const member = await this.findChatMember(chatId, currentUserId);
    if (!member) return;

    await lastValueFrom(this.http.put(`${environment.apiUrl}/chat-members/${member.id}`, {
      data: {
        last_read_at: new Date().toISOString()
      }
    }));
  }

  async deleteChat(chatId: number): Promise<void> {
    await lastValueFrom(this.http.delete(`${environment.apiUrl}/chats/${chatId}`));
  }

  private async findOrCreateDirectChat(currentUserId: number, recipientId: number): Promise<Chat> {
    const directKey = this.directChatKey(currentUserId, recipientId);
    const query = qs.stringify({
      filters: {
        direct_key: {
          $eq: directKey
        }
      },
      pagination: {
        pageSize: 1
      }
    }, { encodeValuesOnly: true });

    const existingChats = await lastValueFrom(this.http.get<ResponseModel<Chat>>(`${environment.apiUrl}/chats?${query}`));
    if (existingChats.data.length > 0) {
      return existingChats.data[0];
    }

    const response = await lastValueFrom(this.http.post<SingleResponseModel<Chat>>(`${environment.apiUrl}/chats`, {
      data: {
        type: 'direct',
        direct_key: directKey
      }
    }));

    return response.data;
  }

  private async createGroupChat(): Promise<Chat> {
    const response = await lastValueFrom(this.http.post<SingleResponseModel<Chat>>(`${environment.apiUrl}/chats`, {
      data: {
        type: 'group'
      }
    }));

    return response.data;
  }

  private async ensureChatMember(chatId: number, userId: number, read: boolean): Promise<void> {
    const existingMember = await this.findChatMember(chatId, userId);
    if (existingMember) return;

    await lastValueFrom(this.http.post(`${environment.apiUrl}/chat-members`, {
      data: {
        chat: chatId,
        user: userId,
        role: 'member',
        joined_at: new Date().toISOString(),
        last_read_at: read ? new Date().toISOString() : null
      }
    }));
  }

  private async findChatMember(chatId: number, userId: number): Promise<ChatMember | null> {
    const query = qs.stringify({
      filters: {
        chat: {
          id: {
            $eq: chatId
          }
        },
        user: {
          id: {
            $eq: userId
          }
        }
      },
      pagination: {
        pageSize: 1
      }
    }, { encodeValuesOnly: true });

    const response = await lastValueFrom(this.http.get<ResponseModel<ChatMember>>(`${environment.apiUrl}/chat-members?${query}`));
    return response.data[0] ?? null;
  }

  private async currentUserId(): Promise<number> {
    if (this.authService.user() === null) {
      await this.authService.fetchMe();
    }

    const currentUserId = this.authService.user()?.id;
    if (currentUserId == null) {
      throw new Error('No authenticated user found.');
    }

    return currentUserId;
  }

  private directChatKey(firstUserId: number, secondUserId: number): string {
    return [firstUserId, secondUserId].sort((a, b) => a - b).join(':');
  }
}
